/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/no-explicit-any */

import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import { toast } from 'sonner'
import { getLocaleCookie } from './cookies/getLocaleCookie'

const createQueryString = ({
  queryParams,
}: {
  queryParams: Record<string, string>
}) => {
  let queryString = ''
  for (const [key, value] of Object.entries(queryParams)) {
    if (queryString) {
      queryString += `&${key}=${value}`
    } else {
      queryString += `?${key}=${value}`
    }
  }
  return queryString
}

// Default timeout configurations
const DEFAULT_TIMEOUTS = {
  DEFAULT: 180_000, // increase this to 3 minutes
  ORDER_SUBMISSION: 300_000, // 5 minutes for order submissions
} as const

export type SendRequestType = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD'
  baseURL?: string
  queryParams?: Record<string, string>
  url: string
  data?: any
  token?: string
  withAuth?: boolean
  headers?: Record<string, string>
  returnType?: string
  withErrorHandling?: boolean
  timeout?: number // Add timeout parameter
}

export type HttpToolsType = {
  getCdnUrl: () => string
  getHeaders: ({
    withAuth,
    token,
    headers,
    isHydra,
  }: {
    withAuth: boolean
    token: string
    headers: Record<string, string>
    isHydra: boolean
  }) => Record<string, string>
  sendRequest: <T = HydraErrorType>({
    method,
    baseURL,
    queryParams,
    url,
    data,
    token,
    withAuth,
    headers,
    returnType,
    withErrorHandling,
    timeout,
  }: SendRequestType) => Promise<(T & HydraErrorType) | null>
  DEFAULT_TIMEOUTS: typeof DEFAULT_TIMEOUTS
}

// Create an AbortController with timeout for proper request cancellation
const createTimeoutController = (timeoutMs: number): AbortController => {
  const controller = new AbortController()

  setTimeout(() => {
    controller.abort()
  }, timeoutMs)

  return controller
}

const httpTools: HttpToolsType = {
  getCdnUrl: () => 'https://cdn.unicity.com/config',
  DEFAULT_TIMEOUTS,
  getHeaders: ({
    headers,
    withAuth,
    token,
    isHydra,
  }): Record<string, string> => {
    const jsonHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (withAuth) {
      jsonHeaders.Authorization = `Bearer ${token}`
    }

    if (isHydra) {
      jsonHeaders['x-application'] = 'Shop2.0'
    }

    /**
     * The Accept-Language header is used to determine the language of the request by
     * the API (Hydra). If the header is not set, the server will tend to default to the
     * market or customer's market-- which in the case of PRI is US will result in English.
     * Without providing this header, downstream customer interactions may be in a language
     * that is not expected. This can avoid the need to eager load the customer's
     * locale from the database for every request.
     *
     * Example: password reset emails for PRI users may be triggered from an ES Shop2
     *          password reset form, but the email will be sent in EN due to the implied
     *          US market.
     *
     * @see Octopus/Hydra/Utilities::getLocale() https://github.com/Unicity/Octopus/blob/master/components/services/hydra-app/src/classes/v5/Utilities.php#L162
     */
    const locale: string = headers['Accept-Language'] ?? getLocaleCookie()

    return {
      ...headers,
      ...jsonHeaders,
      'Accept-Language': locale, // ensure the Accept-Language header is always set
    }
  },
  sendRequest: async <T = any>({
    method = 'GET',
    baseURL = process.env.NEXT_PUBLIC_HYDRA_BASE_URL,
    queryParams = {},
    url = '',
    data = null,
    token = '',
    withAuth = true,
    headers = {},
    returnType = 'json',
    withErrorHandling = true,
    timeout,
  }: SendRequestType): Promise<T & HydraErrorType> => {
    type ResponseType = T & HydraErrorType

    // Set default timeout if not provided
    const requestTimeout = timeout ?? DEFAULT_TIMEOUTS.DEFAULT

    try {
      let correctBaseURL = baseURL === 'internal' ? '' : baseURL

      correctBaseURL =
        correctBaseURL === 'jeeves'
          ? process.env.NEXT_PUBLIC_JEEVES_BASE_URL ?? ''
          : correctBaseURL

      const isHydra =
        correctBaseURL?.includes(
          process.env.NEXT_PUBLIC_HYDRA_BASE_URL || 'hydra',
        ) || false

      let query = ''

      if (queryParams) {
        query = createQueryString({ queryParams })
      }

      const requestUrl = `${correctBaseURL}${url}${query}`

      const requestData = JSON.stringify(data)

      // Create AbortController for timeout and cancellation
      const controller = createTimeoutController(requestTimeout)

      // Create fetch request with AbortController signal
      const response = await fetch(requestUrl, {
        method,
        headers: httpTools.getHeaders({ headers, withAuth, token, isHydra }),
        signal: controller.signal, // Add abort signal for proper cancellation
        ...(requestData &&
          (method === 'POST' || method === 'PUT') && { body: requestData }), // only add body if it exists (for POST and PUT requests
      })

      const currentUrl = new URL(window.location.href)
      const pathnameAllSegments = currentUrl.pathname.split('/')
      const pathnameNeededSegments = pathnameAllSegments.slice(0, 3)
      const portUrl = currentUrl.port ? `:${currentUrl.port}` : ''

      const redirectToInvalidUrl = `${currentUrl.protocol}//${
        currentUrl.hostname
      }${portUrl}${pathnameNeededSegments.join('/')}/session-expired`

      if (returnType === 'json') {
        const responseData: ResponseType =
          (await response.json()) as ResponseType

        if (withErrorHandling && responseData.error) {
          toast.error(
            `${responseData.error.code}: ${responseData.error.message}`,
          )
        }

        if (responseData.error?.error_message === 'Invalid Bearer Token.') {
          window.location.href = redirectToInvalidUrl
          return null as unknown as ResponseType
        }

        return responseData
      }

      if (returnType === 'response') {
        const responseWithTypes = response as unknown as ResponseType

        if (withErrorHandling && responseWithTypes.error) {
          toast.error(
            `${responseWithTypes.error.code}: ${responseWithTypes.error.message}`,
          )
        }

        if (
          responseWithTypes.error?.error_message === 'Invalid Bearer Token.'
        ) {
          window.location.href = redirectToInvalidUrl
          return null as unknown as ResponseType
        }

        return responseWithTypes
      }

      if (returnType === 'blob') {
        return response.blob() as unknown as ResponseType
      }

      return null as unknown as ResponseType
    } catch (error) {
      // Handle AbortError (timeout/cancellation) specifically
      if (error instanceof Error && error.name === 'AbortError') {
        if (withErrorHandling) {
          toast.error(
            `Request timed out after ${requestTimeout / 1000} seconds`,
          )
        }

        // Return a timeout error in the expected format
        return {
          error: {
            code: 408,
            message: 'Request timeout',
            error_message: `Request timed out after ${requestTimeout}ms`,
          },
        } as unknown as ResponseType
      }

      return null as unknown as ResponseType
    }
  },
}

export default httpTools
